/* import __COLOCATED_TEMPLATE__ from './voice-language-selector.hbs'; */
/* RESPONSIBLE TEAM: team-fin-voice */
import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import type IntlService from 'embercom/services/intl';
import {
  type FinVoiceVoice,
  DEFAULT_VOICE,
  doesVoiceSupportLanguage,
  FIN_VOICE_11LABS_VOICES,
  getVoicesForLanguage,
} from 'embercom/objects/ai-agent/fin-voice/fin-voice-voice-options';
import {
  FIN_VOICE_VOICE_LANGUAGE_OPTIONS,
  DEFAULT_VOICE_LANGUAGE,
  type FinVoiceVoiceLanguage,
} from 'embercom/objects/ai-agent/fin-voice/fin-voice-language-options';

interface Args {
  selectedVoice?: FinVoiceVoice;
  selectedLanguage?: FinVoiceVoiceLanguage;
  onChange: (voice: FinVoiceVoice, language: FinVoiceVoiceLanguage) => void;
  isVoiceDisabled?: boolean;
  isLanguageDisabled?: boolean;
  voiceDataTestAttribute?: string;
  languageDataTestAttribute?: string;
}

interface Signature {
  Element: HTMLElement;
  Args: Args;
}

export default class VoiceLanguageSelector extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare appService: AppService;

  @tracked _selectedVoice: FinVoiceVoice;
  @tracked _selectedLanguage: FinVoiceVoiceLanguage;
  @tracked voiceOptions: { value: FinVoiceVoice; text: string; description: string }[] = [];

  constructor(owner: unknown, args: Signature['Args']) {
    super(owner, args);

    this._selectedLanguage = args.selectedLanguage || DEFAULT_VOICE_LANGUAGE;
    this._selectedVoice = args.selectedVoice || this.defaultVoice;

    this.updateLanguage(args.selectedLanguage || DEFAULT_VOICE_LANGUAGE);
  }

  get defaultVoice(): FinVoiceVoice {
    if (this.appService.app.canUseFinVoiceDefaultVoiceLydia) {
      // eslint-disable-next-line @intercom/intercom/no-bare-strings
      return 'Lydia';
    }
    return DEFAULT_VOICE;
  }

  get selectedVoice() {
    return this._selectedVoice;
  }

  get selectedLanguage() {
    return this._selectedLanguage;
  }

  get languageOptions() {
    let options = FIN_VOICE_VOICE_LANGUAGE_OPTIONS;

    // Filter out Tamil if the feature flag is not enabled
    if (!this.appService.app.canUseFinVoiceTamilSupport) {
      options = options.filter((option) => option.value !== 'ta-IN');
    }

    return options
      .map((option) => ({
        ...option,
        text: this.intl.t(option.text),
      }))
      .sort((a, b) => a.text.localeCompare(b.text));
  }

  get activeVoiceDescription() {
    if (!this._selectedVoice) {
      return '';
    }

    let voiceData = FIN_VOICE_11LABS_VOICES.find((voice) => voice.value === this._selectedVoice);
    if (!voiceData) {
      return '';
    }

    return voiceData.description.map((desc) => this.intl.t(desc)).join(' ');
  }

  private _updateVoiceOptions() {
    let voiceOptions = getVoicesForLanguage(this._selectedLanguage).map((voice) => ({
      value: voice.value,
      text: voice.text,
      description: voice.description.map((desc) => this.intl.t(desc)).join(' '),
    }));

    this.voiceOptions = voiceOptions;
  }

  @action
  updateLanguage(language: FinVoiceVoiceLanguage) {
    this._selectedLanguage = language;
    this._updateVoiceOptions();

    // Check if current voice supports the new language
    if (this._selectedVoice && !doesVoiceSupportLanguage(this._selectedVoice, language)) {
      // Reset voice selection only if it doesn't support the new language
      this._selectedVoice = doesVoiceSupportLanguage(this.defaultVoice, language)
        ? this.defaultVoice
        : this.voiceOptions[0]?.value;
    }

    this.args.onChange(this._selectedVoice, this._selectedLanguage);
  }

  @action
  updateVoice(voice: FinVoiceVoice) {
    this._selectedVoice = voice;
    this.args.onChange(this._selectedVoice, this._selectedLanguage);
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Operator::Fin::Setup::Components::VoiceLanguageSelector': typeof VoiceLanguageSelector;
  }
}
